import type { ReactNode } from 'react';

import AdminPageHero, { ADMIN_HERO_BACKGROUND, type AdminPageHeroSideCard } from './AdminPageHero';

export type { AdminPageHeroSideCard };

export type AdminPageShellProps = {
  /** Titre affiché dans le hero (souvent = libellé de la page / breadcrumb). */
  title: string;
  description?: string | null;
  primaryAction?: ReactNode;
  /** Carte latérale du hero (indicateur contextuel). Absente = bannière pleine largeur. */
  sideCard?: AdminPageHeroSideCard | null;
  /** @deprecated Actualiser via la barre supérieure (useRegisterPortalRefresh). */
  onRefresh?: () => void;
  onRetry?: () => void;
  isLoading?: boolean;
  isError?: boolean;
  loadingMessage?: string;
  errorMessage?: string;
  /** Contenu au-dessus du hero (ex. « Bonjour » sur le tableau de bord). */
  topSlot?: ReactNode;
  /** Image de fond du hero (`public/images/…`). */
  backgroundImage?: string;
  children?: ReactNode;
};

/**
 * Enveloppe standard des pages admin / apprenant : hero puis contenu (actualiser = barre supérieure).
 */
export default function AdminPageShell({
  title,
  description = null,
  primaryAction,
  sideCard,
  onRefresh,
  onRetry,
  isLoading = false,
  isError = false,
  loadingMessage = 'Chargement…',
  errorMessage = 'Impossible de charger les données.',
  topSlot,
  backgroundImage = ADMIN_HERO_BACKGROUND,
  children,
}: AdminPageShellProps) {
  const retry = onRetry ?? onRefresh;

  const hero = (
    <AdminPageHero
      title={title}
      description={description}
      primaryAction={primaryAction}
      sideCard={sideCard}
      backgroundImage={backgroundImage}
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {topSlot}
        {hero}
        <div className="flex min-h-[120px] items-center justify-center rounded-xl bg-white shadow-sm">
          <p className="text-sm text-on-surface-variant">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        {topSlot}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {retry ? (
            <button
              type="button"
              onClick={retry}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white"
            >
              Réessayer
            </button>
          ) : null}
        </div>
        {hero}
        <p className="text-sm text-on-surface-variant">{errorMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {topSlot}
      {hero}
      {children}
    </div>
  );
}
